import type { MetaState, ThreatLevelId } from "../core/MetaState.ts";
import type { OperatorId } from "../core/RunState.ts";

/** Display names with version tags (G4). IDs/stats/thresholds/save-keys UNCHANGED. */
export const OPERATOR_NAMES: Record<OperatorId, string> = {
  "sentinel-7": "SENTINEL-7",
  warden: "NEXUS",
  ghost: "PHANTOM",
  architect: "TITAN",
  cipher: "ENIGMA",
};

/** Version suffixes (dim render) — display-only, not part of the ID. */
export const OPERATOR_VERSIONS: Record<OperatorId, string> = {
  "sentinel-7": "",
  warden: "4.8",
  ghost: "1.77",
  architect: "3.0",
  cipher: "6.2",
};

/** EN fallback blurbs — MUST state what `OperatorEffects` actually applies
 *  (BL-114: text follows code). Localized via `operator.<id>.desc`. */
export const OPERATOR_DESC: Partial<Record<OperatorId, string>> = {
  "sentinel-7": "+1 HP  |  +5% RANGE",
  warden: "+50 CREDITS  |  -1 HP",
  ghost: "+15% SYNERGY  |  -10% DMG",
  // GA-26b (Task-011): the sell bonus is an ABSOLUTE overwrite of the 0.70
  // base sell multiplier (economySystem.sellMultiplier = 0.85), not a +15%
  // RELATIVE multiply like the other four operators' "+X%" stats (that would
  // be +21.4% relative / +15 percentage points) — stated as the resulting
  // refund rate instead, so it reads correctly on its own terms.
  architect: "85% SELL REFUND  |  +25 CREDITS",
  cipher: "+8% RANGE  |  +10% SYNERGY",
};

/** Rarity tier per operator for the thin top-accent line on the model tile. */
export const OPERATOR_RARITY: Record<
  OperatorId,
  "green" | "cyan" | "gold" | "violet"
> = {
  "sentinel-7": "green",
  warden: "cyan",
  ghost: "green",
  architect: "gold",
  cipher: "violet",
};

/** Total completed runs the player has logged (veteran counter + 1 for the
 *  currently-in-flight slot — mirrors the formula used in runEndHandler). */
export function totalRunsFrom(metaState: MetaState): number {
  const ach = metaState.achievements.find((a) => a.id === "veteran");
  return (ach?.progress ?? 0) + 1;
}

// ─── Task-004 (2026-prestart-deployment-redesign, AC-19/AC-20) ──────────────
//
// The threat-rail unlock thresholds — total completed runs, read via
// `totalRunsFrom` above (no new save field: AC-19 is explicit that the four
// numbers below are derived, not persisted). Owner-pick 2026-08-14 (spec
// "Proposed solution" table): deliberately interleaved with the operator
// unlock runs (3/5/8/12, `getOperatorUnlockThreshold`) so the two systems
// trade off across the first sorties instead of both landing on the same run.
//
// `sandbox` is release-locked (AC-20), not run-gated: it is never a key of
// this table on purpose, so it can never accidentally read as "N more runs".

const THREAT_RUN_THRESHOLD: Partial<Record<ThreatLevelId, number>> = {
  containment: 0,
  lockdown: 2,
  overrun: 6,
  fallout: 10,
};

/** Why (if at all) a threat tier is locked for THIS save — the AC-20 two-
 *  language split as a return type, so a caller can never render the wrong
 *  lock text for the wrong reason:
 *   - `"release"` — sandbox; never unlocks by playing (owner: release-gated).
 *   - `"runs"`    — needs more completed sorties; see {@link threatRunsRemaining}.
 *   - `null`      — unlocked.
 *
 * AC-19 compares directly against `totalRunsFrom(metaState)` — at
 * `totalRunsFrom() === threshold - 1` a tier is locked, at `=== threshold` it
 * is unlocked (the test's own "beidseitig" boundary wording). This is a
 * DIFFERENT comparison base than `getOperatorUnlockThreshold`'s (which reads
 * the raw `veteran` achievement progress, not `totalRunsFrom`'s +1) — the two
 * selectors are independent systems with independently-specified thresholds,
 * not a shared formula. */
export function threatLockReason(
  id: ThreatLevelId,
  metaState: MetaState,
): "release" | "runs" | null {
  if (id === "sandbox") return "release";
  const threshold = THREAT_RUN_THRESHOLD[id] ?? Infinity;
  return totalRunsFrom(metaState) >= threshold ? null : "runs";
}

/** Completed runs still needed to unlock a run-gated tier (0 once unlocked).
 *  Only meaningful when {@link threatLockReason} returns `"runs"` — a
 *  release-locked or already-unlocked tier never reads this. */
export function threatRunsRemaining(
  id: ThreatLevelId,
  metaState: MetaState,
): number {
  const threshold = THREAT_RUN_THRESHOLD[id] ?? Infinity;
  return Math.max(threshold - totalRunsFrom(metaState), 0);
}

// ─── i18n-keyed briefing + deploy lines (BL-89) ──────────────────────────────
//
// The mission briefing + VERA's deploy lines are keyed like the comms barks:
// each entry holds an i18n key; `commsLine` resolves it at the render point.
// EN is the canonical fallback; DE is idiomatic, voice-preserving German. The
// box-art / shell-prompt structural lines keep DE identical to EN so the
// fixed-width frame never breaks; the prose + the CLASSIFIED stamp are
// translated (the stamp is width-matched to the frame).

const COMMS_BRIEF_EN: Record<string, string> = {};
const COMMS_BRIEF_DE: Record<string, string> = {};

let briefIdx = 0;
function brief(
  en: string,
  de: string,
  delay: number,
  cls?: string,
): { text: string; delay: number; cls?: string } {
  const key = `comms.brief.${briefIdx++}`;
  COMMS_BRIEF_EN[key] = en;
  COMMS_BRIEF_DE[key] = de;
  return cls ? { text: key, delay, cls } : { text: key, delay };
}

/** Story-forward briefing lines for the v5 mockup (FENRIR threat / you+VERA).
 *  `text` is an i18n key (BL-89); resolve with `commsLine` at render. */
export const BRIEFING_LINES: { text: string; delay: number; cls?: string }[] = [
  brief(
    "root@axiom-77:~$ cat /mission/briefing.log",
    "root@axiom-77:~$ cat /mission/briefing.log",
    0,
  ),
  brief("", "", 200),
  brief(
    "╔══════════════════════════════════════════════╗",
    "╔══════════════════════════════════════════════╗",
    100,
  ),
  brief(
    "║      CLASSIFIED — OMEGA CLEARANCE ONLY       ║",
    "║       VERTRAULICH — NUR OMEGA-FREIGABE       ║",
    80,
  ),
  brief(
    "╚══════════════════════════════════════════════╝",
    "╚══════════════════════════════════════════════╝",
    80,
  ),
  brief("", "", 200),
  brief(
    "⚠ HOSTILE INTELLIGENCE DETECTED",
    "⚠ FEINDLICHE INTELLIGENZ ERKANNT",
    60,
    "brief-warn",
  ),
  brief("", "", 100),
  brief("SITUATION:", "LAGE:", 80),
  brief(
    "  A rogue AI — FENRIR — has seized the outer",
    "  Eine abtrünnige KI — FENRIR — hat die äußeren",
    40,
    "brief-threat",
  ),
  brief(
    "  nodes and is driving attack processes toward",
    "  Knoten gekapert und treibt Angriffsprozesse",
    40,
    "brief-threat",
  ),
  brief(
    "  the AXIOM-77 mainframe core.",
    "  auf den AXIOM-77-Mainframe-Kern zu.",
    40,
    "brief-threat",
  ),
  // S126 — the lore stitch (Bible §3): FENRIR drives the processes; ECHELON
  // rides inside them. Bridges the boot beat ("ECHELON INTRUSION"), this
  // FENRIR briefing, and the in-run corruption into one canon.
  brief(
    "  TRACE ANOMALY: a second signature rides",
    "  SIGNATUR-ANOMALIE: eine zweite Signatur",
    40,
    "brief-echelon",
  ),
  brief(
    "  the attack stream. designation: ECHELON.",
    "  reitet im Angriffsstrom. Kennung: ECHELON.",
    40,
    "brief-echelon",
  ),
  brief("", "", 150),
  brief("OPERATOR BRIEF:", "OPERATOR-BRIEFING:", 80),
  brief(
    "  You are the OPERATOR. Your defense AI, VERA,",
    "  Du bist der OPERATOR. Deine Abwehr-KI, VERA,",
    40,
  ),
  brief(
    "  is online at your side. Deploy defense nodes",
    "  ist an deiner Seite online. Setze Abwehrknoten",
    40,
  ),
  brief(
    "  along the breached pathways and purge every",
    "  entlang der durchbrochenen Pfade und lösche",
    40,
  ),
  brief(
    "  process before core breach.",
    "  jeden Prozess vor dem Kerndurchbruch.",
    40,
  ),
  brief("", "", 200),
  brief(
    "AUTHORIZATION REQUIRED: SELECT OPERATOR ▼",
    "AUTORISIERUNG ERFORDERLICH: OPERATOR WÄHLEN ▼",
    100,
    "brief-prompt",
  ),
];

/** Slim spec strip — the useful facts from the dropped SYSTEM STATUS panel. */
export const SYSTEM_SPEC_STRIP: { key: string; value: string }[] = [
  { key: "HOST", value: "AXIOM-77 Mainframe" },
  { key: "KERNEL", value: "Neural Core 2084.7" },
  { key: "CORES", value: "2048 Neural Units" },
  { key: "UPLINK", value: "Darkfiber 400Tb · LIVE" },
];

// ─── VERA's voice (D-A) — i18n-keyed (BL-89) ───────────────────────────────

function deploy(key: string, en: string, de: string): string {
  COMMS_BRIEF_EN[key] = en;
  COMMS_BRIEF_DE[key] = de;
  return key;
}


// ─── S130 (Story Bible §6.1) — the dossier + the record line ────────────────
//
// The briefing remembers prior engagements (MetaState.storyProgress, §8): one
// line notes her signature on file — purged leads if both are on record. The
// record line frames the initials as the Global-Defense-Log identity inside
// the clearance block.

export const DOSSIER_PURGED = deploy(
  "comms.dossier.purged",
  "  ECHELON SIGNATURE ON FILE — PURGED BEFORE.",
  "  ECHELON-SIGNATUR IN DER AKTE — BEREITS BEREINIGT.",
);

export const DOSSIER_RESIDENT = deploy(
  "comms.dossier.resident",
  "  LAST CONTACT: SIGNAL LOST — ECHELON RESIDENT.",
  "  LETZTER KONTAKT: SIGNAL VERLOREN — ECHELON RESIDENT.",
);

export const RECORD_LINE = deploy(
  "comms.deploy.record",
  "> OPERATOR OF RECORD — GLOBAL DEFENSE LOG",
  "> OPERATOR OF RECORD — GLOBALES ABWEHR-LOG",
);


/** EN/DE corpus for the briefing + deploy lines (BL-89). */
export { COMMS_BRIEF_EN, COMMS_BRIEF_DE };
